import React, { useEffect, useState } from "react";
import {
  Table,
  Spinner,
  Alert,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import API from "../api";

export default function ScheduleManager() {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [newWeek, setNewWeek] = useState({
    week_number: "",
    date: "",
    type: "Fun Round",
  });

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!user?.leagueid) {
        setError("No league assigned.");
        setLoading(false);
        return;
      }

      try {
        const res = await API.get(
          `/league/schedule/${user.leagueid}/schedule-grid`
        );
        setSchedule(res.data);
      } catch (err) {
        setError("Failed to load schedule");
        console.error("Schedule load error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, [user?.leagueid]);

  const handleAddWeek = async () => {
    try {
      await API.post(
        `/league/${user.leagueid}/add-special-week`,
        newWeek
      );
      setShowModal(false);
      setNewWeek({ week_number: "", date: "", type: "Fun Round" });
      const updated = await API.get(
        `/league/schedule/${user.leagueid}/schedule-grid`
      );
      setSchedule(updated.data);
    } catch (err) {
      alert("Failed to add week");
    }
  };

  const [pairing, setPairing] = useState(null); // { week_number, matches, standings_through_week }
  const [pairingWeek, setPairingWeek] = useState(null);

  const handlePreviewPairings = async (week_number) => {
    setPairingWeek(week_number);
    setPairing(null);
    try {
      const res = await API.get(
        `/league/${user.leagueid}/position-round/${week_number}/preview`
      );
      setPairing(res.data);
    } catch (err) {
      setPairingWeek(null);
      alert(err.response?.data?.error || "Failed to build pairings");
    }
  };

  const handleGeneratePairings = async (force = false) => {
    try {
      const res = await API.post(
        `/league/${user.leagueid}/position-round/${pairingWeek}/generate` +
          (force ? "?force=1" : "")
      );
      setPairing(null);
      setPairingWeek(null);
      alert(res.data.message);
      const updated = await API.get(
        `/league/schedule/${user.leagueid}/schedule-grid`
      );
      setSchedule(updated.data);
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to save pairings";
      if (
        err.response?.status === 409 &&
        window.confirm(`${msg}\n\nRe-pair anyway?`)
      ) {
        handleGeneratePairings(true);
      } else {
        alert(msg);
      }
    }
  };

  const handleDeleteWeek = async (week_number) => {
    if (!window.confirm("Are you sure you want to delete this special week?"))
      return;

    try {
      await API.delete(
        `/league/${user.leagueid}/delete-week/${week_number}`
      );
      const updated = await API.get(
        `/league/schedule/${user.leagueid}/schedule-grid`
      );
      setSchedule(updated.data);
    } catch (err) {
      alert("Failed to delete week");
    }
  };

  if (loading) return <Spinner animation="border" className="m-5" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!schedule) return null;

  const { teams, weeks, matchups } = schedule;

  const isSpecialWeek = (week) =>
    week.type === "Fun Round" || week.type === "Position Round";

  return (
    <div className="schedule-manager mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Manage League Schedule</h4>
        <Button size="sm" variant="primary" onClick={() => setShowModal(true)}>
          Add Special Round
        </Button>
      </div>

      <Table striped bordered hover responsive className="text-center">
        <thead>
          <tr>
            <th>Week</th>
            <th>Date</th>
            {teams.map((teamId) => (
              <th key={teamId}>{teamId}</th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => {
            const dateStr = new Date(w.date + "T00:00:00").toLocaleDateString(
              "en-US"
            );

            if (isSpecialWeek(w)) {
              return (
                <tr key={w.week_number} className="table-info fw-bold text-center">
                  <td>{w.week_number}</td>
                  <td>{dateStr}</td>
                  <td colSpan={teams.length}>
                    {w.type === "Fun Round" && "🎉 Fun Round"}
                    {w.type === "Position Round" && "Position Round"}
                  </td>
                  <td>
                    {w.type === "Position Round" && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => handlePreviewPairings(w.week_number)}
                      >
                        Pairings
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteWeek(w.week_number)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              );
            }

            return (
              <tr key={w.week_number}>
                <td>{w.week_number}</td>
                <td>{dateStr}</td>
                {teams.map((teamId) => {
                  const match = matchups.find(
                    (m) =>
                      m.week_number === w.week_number &&
                      (m.team1 === teamId || m.team2 === teamId)
                  );
                  return (
                    <td key={teamId}>
                      {match
                        ? match.is_bye
                          ? "BYE"
                          : match.team1 === teamId
                          ? match.team2
                          : match.team1
                        : ""}
                    </td>
                  );
                })}
                <td>-</td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Add Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Special Round</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Week Number</Form.Label>
              <Form.Control
                type="number"
                value={newWeek.week_number}
                onChange={(e) =>
                  setNewWeek({ ...newWeek, week_number: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mt-2">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={newWeek.date}
                onChange={(e) =>
                  setNewWeek({ ...newWeek, date: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mt-2">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={newWeek.type}
                onChange={(e) =>
                  setNewWeek({ ...newWeek, type: e.target.value })
                }
              >
                <option value="Fun Round">Fun Round</option>
                <option value="Position Round">Position Round</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddWeek}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Position Round pairings */}
      <Modal
        show={!!pairingWeek}
        onHide={() => {
          setPairingWeek(null);
          setPairing(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Position Round &mdash; Week {pairingWeek}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!pairing ? (
            <Spinner animation="border" />
          ) : (
            <>
              <p className="text-muted mb-2">
                Standings through week {pairing.standings_through_week}.
              </p>
              <Alert variant="light" className="py-2 small">
                These refresh automatically from the standings every time the
                week's scorecards or score entry are opened, so they stay
                current until the round is played. Saving now just records them
                early &mdash; later results will still re-pair them.
              </Alert>
              <Table size="sm" bordered className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Match</th>
                    <th>Teams</th>
                  </tr>
                </thead>
                <tbody>
                  {pairing.matches.map((m) => (
                    <tr key={m.match_number}>
                      <td>{m.match_number}</td>
                      <td>{m.description}</td>
                      <td className="text-start">
                        {m.team1_label} ({m.team1_points})
                        {m.team2 ? (
                          <>
                            {" vs "}
                            {m.team2_label} ({m.team2_points})
                          </>
                        ) : (
                          " — bye"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setPairingWeek(null);
              setPairing(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!pairing}
            onClick={() => handleGeneratePairings(false)}
          >
            Save Pairings
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
